import { DimensionDetails, TgkAmountObjectSource } from "../../interfaces/CoObject";
import React, { useEffect, useState } from "react";
import { Dimension } from "../../../api/models/dimension";
import LoadingManager from "../../../common/loading/LoadingManager";
import { AMOUNT_OBJECT_SOURCE } from "../../../utils/constants/notification";
import { SourceTypeEnum } from "../../../api/models/source-type-enum";
import { IChoiceGroupOption, IComboBoxOption } from "@fluentui/react";
import { fetchDimensions } from "../../../api/object/Dimensions";
import { ObjectPanelTabs } from "../co-panel/ObjectPanelComponent";
import { amountSourceTypeEnumOptions } from "../SourceTypeEnumOptions";

const useCoObjectSourceAmountPanel = (props) => {
  const {
    objectSource: _objectSource,
    updateCoObjectSourceAmount,
    inheritableParameters,
    isFileExcelExisting,
    fileExcelName,
    updateFileExcelName,
    isEdit,
    tab,
    isComponentInstance,
    isLocked,
    datasetFields,
  } = props;

  //copying required to not propagate unwanted changes
  const objectSource = {
    ..._objectSource,
    type: _objectSource?.type ?? "AMOUNT",
    dimensions: _objectSource?.dimensions?.map((d) => {
      return {
        ...d,
        values: d.values.map((v) => v),
      };
    }),
    analyticalWorkspace: {
      code: _objectSource?.analyticalWorkspace?.code,
      description: _objectSource?.analyticalWorkspace?.description,
    },
    dataset: {
      code: _objectSource?.dataset?.code,
      description: _objectSource?.dataset?.description,
      partitionType: _objectSource?.dataset?.partitionType,
    },
    amount: _objectSource?.amount,
  };

  const [
    { userSelectedDimensionsItems, mandatoryDimensionsItems, isContextualMenuOpen, dimensionsRoot },
    setUserSelectedDimensionsState,
  ] = useState<{
    userSelectedDimensionsItems: Dimension[];
    mandatoryDimensionsItems: Dimension[];
    isContextualMenuOpen: boolean;
    dimensionsRoot: Dimension[];
  }>({
    userSelectedDimensionsItems: [],
    mandatoryDimensionsItems: [],
    isContextualMenuOpen: false,
    dimensionsRoot: [],
  });

  useEffect(() => {
    let objSource: TgkAmountObjectSource;
    if (
      objectSource?.objectSource !== SourceTypeEnum.EXCEL &&
      objectSource?.objectSource !== SourceTypeEnum.TAGETIK_FORM &&
      objectSource?.dbApplication
    ) {
      selectDBAndFetchDefaultRoots({ key: objectSource.dbApplication.id, text: objectSource.dbApplication.desc });
      return () => LoadingManager.remove(AMOUNT_OBJECT_SOURCE);
    } else if (!objectSource) {
      objSource = {
        type: "AMOUNT",
        form: null,
        sheet: null,
        area: null,
        objectSource: SourceTypeEnum.TAGETIK,
        dbApplication: null,
        dimensions: [],
        analyticalWorkspace: null,
        dataset: null,
        amount: null,
      };
    } else {
      objSource = { ...objectSource };
    }
    updateCoObjectSourceAmount(objSource, validateFields(objSource, mandatoryDimensionsItems), false);
    return () => LoadingManager.remove(AMOUNT_OBJECT_SOURCE);
  }, []);

  useEffect(() => {
    if (
      objectSource?.objectSource !== SourceTypeEnum.EXCEL &&
      objectSource?.objectSource !== SourceTypeEnum.TAGETIK_FORM &&
      objectSource?.dbApplication?.id &&
      inheritableParameters &&
      mandatoryDimensionsItems &&
      !isEdit &&
      objectSource?.dimensions.length === 0
    ) {
      const objSource: TgkAmountObjectSource = {
        ...objectSource,
        dimensions: [],
      };
      objSource.dimensions = inheritableParameters
        ?.filter((d) => mandatoryDimensionsItems.find((item) => d.dimensionOid === item.oid))
        .map((p) => ({ ...p, inherited: true }));
      updateCoObjectSourceAmount(objSource, validateFields(objSource, mandatoryDimensionsItems), false);
    }

    return () => LoadingManager.remove(AMOUNT_OBJECT_SOURCE);
  }, [inheritableParameters, mandatoryDimensionsItems, objectSource?.dbApplication?.id]);

  async function selectDBAndFetchDefaultRoots(option: IComboBoxOption) {
    LoadingManager.create(AMOUNT_OBJECT_SOURCE);
    const modified = objectSource.dbApplication?.id !== option.key.toString();

    let obj: TgkAmountObjectSource = {
      ...objectSource,
      dbApplication: { id: String(option.key), desc: option.text },
      dimensions: modified ? [] : objectSource.dimensions,
    };

    try {
      const dimensions = await fetchDimensions(option.key.toString());

      const defaultDimensionsItems = dimensions.filter((dimension) => dimension.mandatory);
      const userDimensionsItems = dimensions.filter((dimension) => {
        const isMandatory = dimension.mandatory;
        const hasValues =
          objectSource.dimensions?.find((selectedDimension) => dimension.oid === selectedDimension.dimensionOid) !==
          undefined;
        return !isMandatory && hasValues;
      });
      setUserSelectedDimensionsState((s) => ({
        ...s,
        dimensionsRoot: dimensions,
        mandatoryDimensionsItems: defaultDimensionsItems,
        userSelectedDimensionsItems: userDimensionsItems,
      }));

      updateCoObjectSourceAmount(obj, validateFields(obj, defaultDimensionsItems), modified);
    } catch (e) {
      // eslint-disable-next-line no-undef
      console.error(e);
      updateCoObjectSourceAmount(obj, validateFields(obj, mandatoryDimensionsItems), modified);
    } finally {
      LoadingManager.remove(AMOUNT_OBJECT_SOURCE);
    }
  }

  function updateDimensionFromTree(dimensionItem: Dimension, newDimensions: DimensionDetails[] | undefined) {
    const dimensionToAdd = newDimensions?.map((newDimension) => ({ ...newDimension, dimensionOid: dimensionItem.oid }));

    const newSourceDimensions = objectSource.dimensions.filter((d) => d.dimensionOid !== dimensionItem.oid);
    newSourceDimensions.push(...dimensionToAdd);
    const obj: TgkAmountObjectSource = {
      ...objectSource,
      dimensions: newSourceDimensions,
    };

    updateCoObjectSourceAmount(obj, validateFields(obj, mandatoryDimensionsItems), true);
  }

  function addDimension(d: Dimension) {
    setUserSelectedDimensionsState((s) => ({
      ...s,
      isContextualMenuOpen: false,
      userSelectedDimensionsItems: [...s.userSelectedDimensionsItems, d],
    }));
    const thisDimensionParameters = inheritableParameters
      .filter((p) => d.oid === p.dimensionOid)
      .map((p) => ({ ...p, inherited: true }));
    const obj: TgkAmountObjectSource = {
      ...objectSource,
      dimensions: [...objectSource.dimensions, ...thisDimensionParameters],
    };
    updateCoObjectSourceAmount(obj, validateFields(obj, mandatoryDimensionsItems), true);
  }

  function removeDimension(dimensionOid: string) {
    const obj: TgkAmountObjectSource = {
      ...objectSource,
      dimensions: objectSource.dimensions.filter((d) => d.dimensionOid !== dimensionOid),
    };
    updateCoObjectSourceAmount(obj, validateFields(obj, mandatoryDimensionsItems), true);
  }

  function validateFields(obj: TgkAmountObjectSource, dimensionsItems?: Dimension[]): ObjectPanelTabs {
    let isValid = true;
    if (obj == null) {
      isValid = false;
    } else {
      const { dbApplication, dimensions } = obj;
      switch (obj?.objectSource) {
        case SourceTypeEnum.TAGETIK_FORM:
          return validateTgkFormSource(obj);
        case SourceTypeEnum.EXCEL:
          return validateExcelSource(obj);
        default:
          if (obj.type === "AMOUNT_AIH") {
            return validateAnalytical(obj);
          } else {
            // @ts-ignore
            if (dbApplication == null || dbApplication === "") {
              isValid = false;
            }
            if (!dimensionsItems?.length) {
              isValid = false;
            }
            dimensionsItems?.forEach((mandatoryDimensionRoot) => {
              if (!dimensions?.find((d) => d.dimensionOid === mandatoryDimensionRoot.oid)?.values?.length) {
                isValid = false;
              }
            });
          }
      }
    }
    return { key: props.tab.key, name: props.tab.name, isValid: isValid };
  }

  const validateExcelSource = (obj: TgkAmountObjectSource): ObjectPanelTabs => {
    const isValid = validateCommonSource(obj);
    return { key: props.tab.key, name: props.tab.name, isValid: isValid };
  };

  const validateTgkFormSource = (obj: TgkAmountObjectSource): ObjectPanelTabs => {
    const isValid = validateCommonSource(obj) && obj.dbApplication != null;
    return { key: props.tab.key, name: props.tab.name, isValid: isValid };
  };

  const validateAnalytical = (obj: TgkAmountObjectSource): ObjectPanelTabs => {
    const tab = { key: props.tab.key, name: props.tab.name, isValid: false };
    if (obj == null) return tab;
    const { dbApplication, analyticalWorkspace, dataset, amount } = obj;
    if (!dbApplication) return tab;
    if (!analyticalWorkspace) return tab;
    if (!dataset) return tab;
    if (!amount) return tab;
    return { ...tab, isValid: true };
  };

  const validateCommonSource = (obj: TgkAmountObjectSource): boolean => {
    if (!obj) return false;

    const { form, sheet, area, cell, type } = obj;
    return (
      form != null &&
      form.oid != null &&
      sheet != null &&
      sheet.desc != null &&
      type != null &&
      objectSource != null &&
      !isAreaAndCellEmpty(area, cell)
    );
  };

  const isAreaAndCellEmpty = (area, cell) => {
    return (!area || !area.name) && !cell;
  };

  const filteredDimensionsRoot = dimensionsRoot.filter(
    (dimension) =>
      [...userSelectedDimensionsItems, ...mandatoryDimensionsItems].find(
        (presentItem) => dimension.desc === presentItem.desc
      ) === undefined //not default and not previously selected
  );

  const onChangeFormattingAs = (_: React.SyntheticEvent<HTMLElement>, option: IChoiceGroupOption) => {
    const selectedOption = String(option.key);
    const obj = {
      type: "AMOUNT",
      objectSource: selectedOption,
      dbApplication: null,
      dimensions: null,
    } as TgkAmountObjectSource;

    objectSource.objectSource = selectedOption;
    updateCoObjectSourceAmount(obj, validateFields(obj, []), true);
  };

  const isExcelSource = () => {
    return objectSource.objectSource === SourceTypeEnum.EXCEL;
  };

  const isTgkSource = () => {
    return (
      objectSource.objectSource === undefined ||
      objectSource.objectSource.length == 0 ||
      objectSource.objectSource === SourceTypeEnum.TAGETIK
    );
  };

  const isTgkFormSource = () => {
    return objectSource.objectSource === SourceTypeEnum.TAGETIK_FORM;
  };

  const linkRef = React.useRef(null);
  return {
    isEdit,
    isLocked,
    tab,
    isComponentInstance,
    objectSource,
    datasetFields,
    amountSourceTypeEnumOptions,
    isExcelSource,
    validateExcelSource,
    isTgkSource,
    isTgkFormSource,
    validateTgkFormSource,
    onChangeFormattingAs,
    updateCoObjectSourceAmount,
    selectDBAndFetchDefaultRoots,
    mandatoryDimensionsItems,
    inheritableParameters,
    validateFields,
    validateAnalytical,
    updateDimensionFromTree,
    userSelectedDimensionsItems,
    removeDimension,
    filteredDimensionsRoot,
    linkRef,
    addDimension,
    setUserSelectedDimensionsState,
    isContextualMenuOpen,
    isFileExcelExisting,
    fileExcelName,
    updateFileExcelName,
  };
};
export default useCoObjectSourceAmountPanel;
